import { useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FullStory, isInitialized } from "@fullstory/browser";

import {
  identifyFullStoryUser,
  isApplicationCapturePath,
  isFullStoryCaptureSafe,
  readLastFullStoryUid,
  subscribeFullStoryIdentity,
} from "src/shared/utilities/fullStorySession";

/**
 * Capture stays on `/application` only. Home, rent, requests, settings,
 * availability, and property pages call shutdown so they do not count
 * against the FullStory quota.
 *
 * Identity is stamped here, not only on the register page. Signup returns
 * from Auth0 onto the form, which never mounted the old identify call, so
 * capture stayed shut down for every wizard.
 */
const AllowedRoutesTracker = ({ fsInitialized }: any) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth0();
  // Re-render when identity is stamped so a deep link onto an application
  // route starts capture in the same turn it becomes safe.
  const captureSafe = useSyncExternalStore(
    subscribeFullStoryIdentity,
    isFullStoryCaptureSafe
  );

  useEffect(() => {
    if (!fsInitialized || !isInitialized()) {
      return;
    }
    // Anything that is not an application wizard stays shut down, even if
    // this browser already has a FullStory uid from a previous application.
    const onApplicationPath = isApplicationCapturePath(location.pathname);
    const uid = user?.sub ? String(user.sub) : "";
    if (onApplicationPath && isAuthenticated && uid) {
      identifyFullStoryUser(uid, { email: user?.email });
    }
    // A persisted uid from someone else must not record this session.
    const identifiedThisUser =
      Boolean(uid) &&
      readLastFullStoryUid() === uid &&
      isFullStoryCaptureSafe();
    if (onApplicationPath && isAuthenticated && identifiedThisUser) {
      FullStory("restart");
    } else {
      FullStory("shutdown");
    }
  }, [location, fsInitialized, isAuthenticated, user, captureSafe]);

  return null;
};

export default AllowedRoutesTracker;
